import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  MapPin,
  CircleNotch,
  ArrowsClockwise,
  User,
  Clock,
  NavigationArrow
} from '@phosphor-icons/react';

const MAP_TILE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

function TechnicianCard({ loc }) {
  const updatedAt = loc.updated_at ? new Date(loc.updated_at) : null;
  const timeDiff = updatedAt ? Math.round((Date.now() - updatedAt.getTime()) / 60000) : null;
  const isRecent = timeDiff !== null && timeDiff < 10;

  return (
    <div className={`bg-white rounded-xl border p-4 ${isRecent ? 'border-green-200' : 'border-slate-200'}`} data-testid={`gps-card-${loc.user_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${isRecent ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
          <p className="font-semibold text-slate-800 text-sm">{loc.user_name}</p>
        </div>
        <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-500 capitalize">{loc.role}</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <MapPin size={12} />
          {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
        </span>
        {loc.accuracy && (
          <span className="flex items-center gap-1">
            <NavigationArrow size={12} />
            ±{loc.accuracy.toFixed(0)}m
          </span>
        )}
      </div>
      {updatedAt && (
        <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
          <Clock size={10} />
          {timeDiff < 1 ? 'Ahora' : `Hace ${timeDiff} min`} &middot; {updatedAt.toLocaleString('es-ES')}
        </p>
      )}
      <a
        href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-blue-600 hover:underline mt-2 inline-block"
        data-testid={`gps-map-link-${loc.user_id}`}
      >
        Ver en Google Maps
      </a>
    </div>
  );
}

export default function GPSTrackingPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLocations = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/api/gps/locations`, { withCredentials: true });
      setLocations(res.data);
    } catch (err) {
      console.error('GPS error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => fetchLocations(false), 60000);
    return () => clearInterval(interval);
  }, [fetchLocations]);

  return (
    <div className="space-y-6" data-testid="gps-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">GPS Tracking</h1>
          <p className="text-slate-500">Ubicacion en tiempo real de tecnicos e ingenieros</p>
        </div>
        <button
          onClick={() => fetchLocations(false)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium disabled:opacity-50"
          data-testid="refresh-gps"
        >
          <ArrowsClockwise size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {/* Map embed */}
      {locations.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="gps-map-container">
          <iframe
            title="Mapa de Tecnicos"
            width="100%"
            height="400"
            style={{ border: 0 }}
            loading="lazy"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${
              Math.min(...locations.map(l => l.longitude)) - 0.05
            }%2C${
              Math.min(...locations.map(l => l.latitude)) - 0.05
            }%2C${
              Math.max(...locations.map(l => l.longitude)) + 0.05
            }%2C${
              Math.max(...locations.map(l => l.latitude)) + 0.05
            }&layer=mapnik&marker=${locations[0].latitude}%2C${locations[0].longitude}`}
          />
        </div>
      )}

      {/* Location cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <CircleNotch size={32} className="animate-spin text-blue-600" />
        </div>
      ) : locations.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map(loc => (
            <TechnicianCard key={loc.user_id} loc={loc} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
          <MapPin size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 font-medium">Sin ubicaciones reportadas</p>
          <p className="text-sm text-slate-400 mt-1">Los tecnicos reportaran su ubicacion automaticamente desde la app</p>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">Como funciona</p>
        <p>Los tecnicos e ingenieros reportan su ubicacion GPS automaticamente cada 60 segundos desde la PWA del CRM. Las ubicaciones se muestran en tiempo real en este panel.</p>
      </div>
    </div>
  );
}
